import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import type {
  AiAgentConfig,
  AiCallingCampaign,
  AiCampaignLead,
  AiCallAttempt,
  AiAgentUsage,
  AiCallingDashboardData,
  CampaignStats,
  CreateAiAgentConfigParams,
  UpdateAiAgentConfigParams,
  CreateCampaignParams,
  UpdateCampaignParams,
  PopulateLeadsParams,
  LeadFilters,
  LeadPreviewResult,
  AiObjectionHandler,
  CreateObjectionHandlerParams,
  UpdateObjectionHandlerParams,
  AiVoice,
  VoiceAiProvidersResponse,
  PromptTemplate,
  PaginatedResponse,
  AiTextingEligibility,
  AiTextingStats,
  AiTextConversation,
  AiTextConversationDetail,
  StartAiTextParams,
} from "../types/ai-calling.types";

/**
 * Unwrap the API response — handleApiCall + extractApiData already strips the
 * outer { statusCode, data } wrapper, so the result is the inner T.
 * This helper provides a single, type-safe extraction point.
 */
function unwrap<T>(result: unknown, fallback?: T): T {
  // extractApiData already unwrapped, but some endpoints double-wrap
  if (result && typeof result === "object" && "data" in result) {
    return (result as Record<string, unknown>).data as T;
  }
  return (result as T) ?? (fallback as T);
}

// ============================================================
// Agent Config
// ============================================================

export const getAgentConfig = async (): Promise<AiAgentConfig | null> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.config), auth: true }),
  );
  return unwrap<AiAgentConfig | null>(result, null);
};

export const createAgentConfig = async (
  data: CreateAiAgentConfigParams,
): Promise<AiAgentConfig> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.config), body: data, auth: true }),
  );
  return unwrap<AiAgentConfig>(result);
};

export const updateAgentConfig = async (
  id: string,
  data: UpdateAiAgentConfigParams,
): Promise<AiAgentConfig> => {
  const result = await handleApiCall(async () =>
    api.patch({ url: getApiUrl(endpoint.aiCalling.configById(id)), body: data, auth: true }),
  );
  return unwrap<AiAgentConfig>(result);
};

export const testAgentConnection = async (
  id: string,
  elevenlabsAgentId: string,
): Promise<{ success: boolean; message: string }> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.testConfig(id)), body: { elevenlabsAgentId }, auth: true }),
  );
  return unwrap<{ success: boolean; message: string }>(result);
};

export const getVoices = async (provider?: string): Promise<AiVoice[]> => {
  const url = provider
    ? `${endpoint.aiCalling.voices}?provider=${encodeURIComponent(provider)}`
    : endpoint.aiCalling.voices;
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(url), auth: true }),
  );
  return unwrap<AiVoice[]>(result, []);
};

export const getProviders = async (): Promise<VoiceAiProvidersResponse> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.providers), auth: true }),
  );
  return unwrap<VoiceAiProvidersResponse>(result, { default: "telnyx", providers: [] });
};

export const getPromptTemplates = async (): Promise<PromptTemplate[]> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.promptTemplates), auth: true }),
  );
  return unwrap<PromptTemplate[]>(result, []);
};

export const generateScript = async (params: {
  campaignType: string;
  agencyName?: string;
  targetAudience?: string;
  tone?: string;
  customInstructions?: string;
}): Promise<{
  systemPrompt: string;
  firstMessage: string;
  objectionHandling: string;
}> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.generateScript), body: params, auth: true }),
  );
  return unwrap(result);
};

// ============================================================
// Campaigns
// ============================================================

export const getCampaigns = async (
  page = 1,
  pageSize = 20,
): Promise<PaginatedResponse<AiCallingCampaign>> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(`${endpoint.aiCalling.campaigns}?page=${page}&pageSize=${pageSize}`), auth: true }),
  );
  return unwrap<PaginatedResponse<AiCallingCampaign>>(result);
};

export const getCampaign = async (id: string): Promise<AiCallingCampaign> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.campaignById(id)), auth: true }),
  );
  return unwrap<AiCallingCampaign>(result);
};

export const createCampaign = async (
  data: CreateCampaignParams,
): Promise<AiCallingCampaign> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.campaigns), body: data, auth: true }),
  );
  return unwrap<AiCallingCampaign>(result);
};

export const updateCampaign = async (
  id: string,
  data: UpdateCampaignParams,
): Promise<AiCallingCampaign> => {
  const result = await handleApiCall(async () =>
    api.patch({ url: getApiUrl(endpoint.aiCalling.campaignById(id)), body: data, auth: true }),
  );
  return unwrap<AiCallingCampaign>(result);
};

export const populateCampaignLeads = async (
  id: string,
  data: PopulateLeadsParams,
): Promise<{ totalPopulated: number }> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.populateLeads(id)), body: data, auth: true }),
  );
  return unwrap<{ totalPopulated: number }>(result);
};

export const startCampaign = async (id: string): Promise<AiCallingCampaign> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.startCampaign(id)), auth: true }),
  );
  return unwrap<AiCallingCampaign>(result);
};

export const pauseCampaign = async (id: string): Promise<AiCallingCampaign> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.pauseCampaign(id)), auth: true }),
  );
  return unwrap<AiCallingCampaign>(result);
};

export const resumeCampaign = async (id: string): Promise<AiCallingCampaign> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.resumeCampaign(id)), auth: true }),
  );
  return unwrap<AiCallingCampaign>(result);
};

export const getCampaignLeads = async (
  id: string,
  status?: string,
  page = 1,
  limit = 20,
): Promise<PaginatedResponse<AiCampaignLead>> => {
  let url = `${endpoint.aiCalling.campaignLeads(id)}?page=${page}&limit=${limit}`;
  if (status) url += `&status=${status}`;

  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(url), auth: true }),
  );
  return unwrap<PaginatedResponse<AiCampaignLead>>(result);
};

export const instantCall = async (
  params: { leadId: string; campaignId?: string },
): Promise<{ success: boolean; message: string }> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.instantCall), body: params, auth: true }),
  );
  return unwrap<{ success: boolean; message: string }>(result);
};

export const getCampaignStats = async (id: string): Promise<CampaignStats> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.campaignStats(id)), auth: true }),
  );
  return unwrap<CampaignStats>(result);
};

// ============================================================
// Monitoring
// ============================================================

export const getDashboard = async (): Promise<AiCallingDashboardData> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.dashboard), auth: true }),
  );
  return unwrap<AiCallingDashboardData>(result);
};

export const getActiveCalls = async (): Promise<AiCallAttempt[]> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.activeCalls), auth: true }),
  );
  return unwrap<AiCallAttempt[]>(result, []);
};

export const getRecentDispositions = async (
  limit = 20,
): Promise<AiCallAttempt[]> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(`${endpoint.aiCalling.recentDispositions}?limit=${limit}`), auth: true }),
  );
  return unwrap<AiCallAttempt[]>(result, []);
};

// ============================================================
// Usage
// ============================================================

export const getCurrentUsage = async (): Promise<AiAgentUsage> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.currentUsage), auth: true }),
  );
  return unwrap<AiAgentUsage>(result);
};

export const getUsageHistory = async (
  startDate?: string,
  endDate?: string,
): Promise<AiAgentUsage[]> => {
  let url = endpoint.aiCalling.usageHistory;
  const params = new URLSearchParams();
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  const queryString = params.toString();
  if (queryString) url += `?${queryString}`;

  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(url), auth: true }),
  );
  return unwrap<AiAgentUsage[]>(result, []);
};

// ============================================================
// Phase 3C: Lead Filter Preview
// ============================================================

export const previewCampaignLeads = async (
  leadFilters: LeadFilters,
): Promise<LeadPreviewResult> => {
  const result = await handleApiCall(async () =>
    api.post({
      url: getApiUrl(endpoint.aiCalling.previewLeads),
      body: { leadFilters },
      auth: true,
    }),
  );
  return unwrap<LeadPreviewResult>(result, { count: 0, sample: [] });
};

// ============================================================
// Phase 3B: Objection Handlers
// ============================================================

export const listObjectionHandlers = async (
  agentConfigId: string,
): Promise<AiObjectionHandler[]> => {
  const result = await handleApiCall(async () =>
    api.get({
      url: `${getApiUrl(endpoint.aiCalling.objections)}?agentConfigId=${encodeURIComponent(agentConfigId)}`,
      auth: true,
    }),
  );
  return unwrap<AiObjectionHandler[]>(result, []);
};

export const createObjectionHandler = async (
  data: CreateObjectionHandlerParams,
): Promise<AiObjectionHandler> => {
  const result = await handleApiCall(async () =>
    api.post({
      url: getApiUrl(endpoint.aiCalling.objections),
      body: data,
      auth: true,
    }),
  );
  return unwrap<AiObjectionHandler>(result);
};

export const updateObjectionHandler = async (
  id: string,
  data: UpdateObjectionHandlerParams,
): Promise<AiObjectionHandler> => {
  const result = await handleApiCall(async () =>
    api.patch({
      url: getApiUrl(endpoint.aiCalling.objectionById(id)),
      body: data,
      auth: true,
    }),
  );
  return unwrap<AiObjectionHandler>(result);
};

export const deleteObjectionHandler = async (
  id: string,
): Promise<{ deleted: boolean }> => {
  const result = await handleApiCall(async () =>
    api.delete({
      url: getApiUrl(endpoint.aiCalling.objectionById(id)),
      auth: true,
    }),
  );
  return unwrap<{ deleted: boolean }>(result, { deleted: false });
};

export const reorderObjectionHandlers = async (
  agentConfigId: string,
  orderedIds: string[],
): Promise<AiObjectionHandler[]> => {
  const result = await handleApiCall(async () =>
    api.post({
      url: getApiUrl(endpoint.aiCalling.reorderObjections),
      body: { aiAgentConfigId: agentConfigId, orderedIds },
      auth: true,
    }),
  );
  return unwrap<AiObjectionHandler[]>(result, []);
};

// ============================================================
// AI Texting (two-way SMS assistant)
// ============================================================

export const getTextingEligibility = async (): Promise<AiTextingEligibility> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.textingEligibility), auth: true }),
  );
  return unwrap<AiTextingEligibility>(result);
};

export const getTextingStats = async (): Promise<AiTextingStats> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.textingStats), auth: true }),
  );
  return unwrap<AiTextingStats>(result);
};

export const getTextConversations = async (
  status?: string,
  page = 1,
  pageSize = 20,
): Promise<PaginatedResponse<AiTextConversation>> => {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (status) params.set("status", status);
  const result = await handleApiCall(async () =>
    api.get({
      url: getApiUrl(`${endpoint.aiCalling.textingConversations}?${params.toString()}`),
      auth: true,
    }),
  );
  return unwrap<PaginatedResponse<AiTextConversation>>(result, {
    items: [],
    meta: { page, pageSize, totalItems: 0, totalPages: 0 },
  });
};

export const getTextConversation = async (id: string): Promise<AiTextConversationDetail> => {
  const result = await handleApiCall(async () =>
    api.get({ url: getApiUrl(endpoint.aiCalling.textingConversationById(id)), auth: true }),
  );
  return unwrap<AiTextConversationDetail>(result);
};

/** POST /texting/start — rejects with code 412 (message set) when the lead can't be texted. */
export const startAiText = async (params: StartAiTextParams): Promise<AiTextConversation> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.textingStart), body: params, auth: true }),
  );
  return unwrap<AiTextConversation>(result);
};

export const takeOverTextConversation = async (id: string): Promise<AiTextConversation> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.textingTakeOver(id)), auth: true }),
  );
  return unwrap<AiTextConversation>(result);
};

export const closeTextConversation = async (id: string): Promise<AiTextConversation> => {
  const result = await handleApiCall(async () =>
    api.post({ url: getApiUrl(endpoint.aiCalling.textingClose(id)), auth: true }),
  );
  return unwrap<AiTextConversation>(result);
};
